import { BREAKDOWN_REASONS, REASONS_REQUIRING_ABSENCE } from 'utils/breakdown';

import { NODE_IDS, SECTION_IDS } from './const';
import { CREATE_BREAKDOWN_FLOW_TEXT } from './texts';
import { isAtOrAfter, isStrictlyBefore } from './utils/timestampOrder';

const PlaceholderStep = () => null;
const SummaryPlaceholder = () => null;

const isReason = (reason) => (d) => d.breakdownReason === reason;
const parallel = (d) => d.parallelRideChosen === true;
const noParallel = (d) => d.parallelRideChosen === false;

const requiresAbsence = (reason) => REASONS_REQUIRING_ABSENCE.includes(reason);

const validateCommon = (d) => {
	const base =
		!!d.fromStationId &&
		!!d.toStationId &&
		!!d.breakdownStationId &&
		!!d.breakdownReason &&
		!!d.incidentTimestamp &&
		(d.parallelRideChosen === true || d.parallelRideChosen === false);
	if (!base) return false;
	if (requiresAbsence(d.breakdownReason)) {
		if (!d.originalDriverAbsence?.fromTimestamp) return false;
		if (!d.originalDriverAbsence?.reasonCode) return false;
		if (!isAtOrAfter(d.originalDriverAbsence.fromTimestamp, d.incidentTimestamp)) return false;
	}
	return true;
};

const validateCushionOrder = (d) =>
	isStrictlyBefore(d.outboundTraveling?.fromTimestamp, d.outboundTraveling?.toTimestamp) &&
	isAtOrAfter(d.outboundTraveling?.fromTimestamp, d.incidentTimestamp);

const validateParallelRide = (d) =>
	!!d.parallelRideId &&
	!!d.outboundTraveling?.fromTimestamp &&
	!!d.outboundTraveling?.toTimestamp &&
	validateCushionOrder(d);

const validateParallelRideOnly = (d) => !!d.parallelRideId;

const validateBusDeadhead = (d) =>
	!!d.deadheadId &&
	!!d.driving?.fromTimestamp &&
	!!d.outboundTraveling?.fromTimestamp &&
	!!d.outboundTraveling?.toTimestamp &&
	!!d.cushioningChoice &&
	validateCushionOrder(d) &&
	isAtOrAfter(d.driving.fromTimestamp, d.incidentTimestamp);

// Full time-order chain across the replacement path:
//   incident <= ETD < ETA <= driving
// ETA > ETD is strict (no instantaneous teleport). Driving >= ETA is inclusive
// (driver can start driving the moment they arrive). Driving >= incident stays
// as a defensive fallback for the case when ETA is not yet filled — the
// wizard should still gate Continue.
const validateReplacementTimeOrder = (d) =>
	isAtOrAfter(d.replacementInboundTraveling.fromTimestamp, d.incidentTimestamp) &&
	isStrictlyBefore(
		d.replacementInboundTraveling.fromTimestamp,
		d.replacementInboundTraveling.toTimestamp
	) &&
	isAtOrAfter(d.driving.fromTimestamp, d.replacementInboundTraveling.toTimestamp) &&
	isAtOrAfter(d.driving.fromTimestamp, d.incidentTimestamp);

const validateFatigueReplacement = (d) =>
	!!d.replacementDriverId &&
	!!d.driving?.fromTimestamp &&
	!!d.replacementInboundTraveling?.fromCityId &&
	!!d.replacementInboundTraveling.fromTimestamp &&
	!!d.replacementInboundTraveling?.toTimestamp &&
	validateReplacementTimeOrder(d);

const validateDriverOutOfHoursReplacement = (d) =>
	!!d.replacementDriverId &&
	!!d.driving?.fromTimestamp &&
	!!d.replacementInboundTraveling?.fromCityId &&
	!!d.replacementInboundTraveling.fromTimestamp &&
	!!d.replacementInboundTraveling?.toTimestamp &&
	!!d.outboundTraveling?.fromTimestamp &&
	!!d.outboundTraveling?.toTimestamp &&
	validateCushionOrder(d) &&
	validateReplacementTimeOrder(d);

/**
 * Breakdown flow graph (v2).
 *
 *   common
 *     ├─ driverFatigue    + parallel  → parallelRide             (ride only; cushion derived from ride)
 *     ├─ busFailure       + parallel  → parallelRideWithCushion  (ride + explicit cushion)
 *     ├─ driverOutOfHours + parallel  → parallelRideWithCushion  (ride + explicit cushion)
 *     ├─ busFailure       + !parallel → busDeadhead
 *     ├─ driverFatigue    + !parallel → fatigueReplacement
 *     └─ driverOutOfHours + !parallel → driverOutOfHoursReplacement
 *
 *   every leaf → summary → external onSubmit
 *
 * Original driver absence (start + optional reason) is collected on the common
 * step when the reason is driverFatigue or driverOutOfHours; the common
 * validate rule enforces its presence.
 *
 * @type {import('common/activeElements/modals/FlowModal/flowTypes').FlowConfig}
 */
export const breakdownFlowConfig = {
	startNodeId: NODE_IDS.common,
	nodes: {
		[NODE_IDS.common]: {
			id: NODE_IDS.common,
			title: CREATE_BREAKDOWN_FLOW_TEXT.modalTitle,
			component: PlaceholderStep,
			validate: validateCommon,
			meta: { subtitle: CREATE_BREAKDOWN_FLOW_TEXT.subtitles.common, scrollableContent: true },
		},
		// Most leaf steps opt into the scrollable-shell variant because
		// their content grows tall. Steps with only an Autocomplete
		// (parallelRide) omit it so the listbox escapes the popup.
		[NODE_IDS.parallelRide]: {
			id: NODE_IDS.parallelRide,
			title: CREATE_BREAKDOWN_FLOW_TEXT.modalTitle,
			component: PlaceholderStep,
			validate: validateParallelRideOnly,
			meta: {
				subtitle: CREATE_BREAKDOWN_FLOW_TEXT.subtitles.parallelRide,
			},
			summarySections: [SECTION_IDS.common, SECTION_IDS.parallelRide],
		},
		[NODE_IDS.parallelRideWithCushion]: {
			id: NODE_IDS.parallelRideWithCushion,
			title: CREATE_BREAKDOWN_FLOW_TEXT.modalTitle,
			component: PlaceholderStep,
			validate: validateParallelRide,
			meta: {
				subtitle: CREATE_BREAKDOWN_FLOW_TEXT.subtitles.parallelRideWithCushion,
			},
			summarySections: [
				SECTION_IDS.common,
				SECTION_IDS.parallelRide,
				SECTION_IDS.cushioningParameters,
			],
		},
		[NODE_IDS.busDeadhead]: {
			id: NODE_IDS.busDeadhead,
			title: CREATE_BREAKDOWN_FLOW_TEXT.modalTitle,
			component: PlaceholderStep,
			validate: validateBusDeadhead,
			meta: {
				subtitle: CREATE_BREAKDOWN_FLOW_TEXT.subtitles.busDeadhead,
				scrollableContent: true,
			},
			summarySections: [
				SECTION_IDS.common,
				SECTION_IDS.deadheadInfo,
				SECTION_IDS.postIncidentDriving,
				SECTION_IDS.cushioningParameters,
			],
		},
		[NODE_IDS.fatigueReplacement]: {
			id: NODE_IDS.fatigueReplacement,
			title: CREATE_BREAKDOWN_FLOW_TEXT.modalTitle,
			component: PlaceholderStep,
			validate: validateFatigueReplacement,
			meta: {
				subtitle: CREATE_BREAKDOWN_FLOW_TEXT.subtitles.fatigueReplacement,
				scrollableContent: true,
			},
			summarySections: [
				SECTION_IDS.common,
				SECTION_IDS.replacementDriverInfo,
				SECTION_IDS.postIncidentDriving,
			],
		},
		[NODE_IDS.driverOutOfHoursReplacement]: {
			id: NODE_IDS.driverOutOfHoursReplacement,
			title: CREATE_BREAKDOWN_FLOW_TEXT.modalTitle,
			component: PlaceholderStep,
			validate: validateDriverOutOfHoursReplacement,
			meta: {
				subtitle: CREATE_BREAKDOWN_FLOW_TEXT.subtitles.driverOutOfHoursReplacement,
				scrollableContent: true,
			},
			summarySections: [
				SECTION_IDS.common,
				SECTION_IDS.replacementDriverInfo,
				SECTION_IDS.postIncidentDriving,
				SECTION_IDS.cushioningParameters,
			],
		},
		[NODE_IDS.summary]: {
			id: NODE_IDS.summary,
			title: CREATE_BREAKDOWN_FLOW_TEXT.modalTitle,
			component: SummaryPlaceholder,
			isSummary: true,
			meta: {
				// Summary has no Autocomplete but can grow tall (every
				// section expanded). Opt into the FlowModal shell variant
				// that keeps HC's default popup scroll on the content pane
				// so long summaries stay inside the popup.
				scrollableContent: true,
			},
		},
	},
	transitions: [
		{
			from: NODE_IDS.common,
			to: NODE_IDS.parallelRide,
			name: 'common_to_parallelRide',
			when: (d) => parallel(d) && isReason(BREAKDOWN_REASONS.driverFatigue)(d),
		},
		{
			from: NODE_IDS.common,
			to: NODE_IDS.parallelRideWithCushion,
			name: 'common_to_parallelRideWithCushion',
			when: (d) =>
				parallel(d) &&
				(isReason(BREAKDOWN_REASONS.busFailure)(d) ||
					isReason(BREAKDOWN_REASONS.driverOutOfHours)(d)),
		},
		{
			from: NODE_IDS.common,
			to: NODE_IDS.busDeadhead,
			name: 'common_to_busDeadhead',
			when: (d) => isReason(BREAKDOWN_REASONS.busFailure)(d) && noParallel(d),
		},
		{
			from: NODE_IDS.common,
			to: NODE_IDS.fatigueReplacement,
			name: 'common_to_fatigueReplacement',
			when: (d) => isReason(BREAKDOWN_REASONS.driverFatigue)(d) && noParallel(d),
		},
		{
			from: NODE_IDS.common,
			to: NODE_IDS.driverOutOfHoursReplacement,
			name: 'common_to_driverOutOfHoursReplacement',
			when: (d) => isReason(BREAKDOWN_REASONS.driverOutOfHours)(d) && noParallel(d),
		},
		{ from: NODE_IDS.parallelRide, to: NODE_IDS.summary, otherwise: true },
		{ from: NODE_IDS.parallelRideWithCushion, to: NODE_IDS.summary, otherwise: true },
		{ from: NODE_IDS.busDeadhead, to: NODE_IDS.summary, otherwise: true },
		{ from: NODE_IDS.fatigueReplacement, to: NODE_IDS.summary, otherwise: true },
		{ from: NODE_IDS.driverOutOfHoursReplacement, to: NODE_IDS.summary, otherwise: true },
	],
};
